import { getPaymentSource, getDepositReceipts } from '../utils/payment-helpers';
import {
  Paper,
  Stack,
  Group,
  Text,
  Button,
  Divider,
  Timeline,
  Loader,
  Alert,
  Tooltip,
  ActionIcon,
  Anchor,
} from '@mantine/core';
import {
  IconCash,
  IconArrowNarrowRight,
  IconPlus,
  IconCheck,
  IconAlertCircle,
  IconNote,
  IconReceipt,
  IconDownload,
} from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import { useCachedBinaryUrl } from '@medplum/react-hooks';
import { ServiceRequest, Attachment } from '@medplum/fhirtypes';
import { usePaymentTracker } from '../hooks/usePaymentTracker';
import { PaymentStatusBadge } from './PaymentStatusBadge';
import {
  PAYMENT_SOURCE_I18N_KEYS,
  ALL_PAYMENT_SOURCES,
  DEFAULT_CURRENCY,
} from '../constants/payment.consts';

import { PaymentModal } from './payment-modals/PaymentModal';
import { RecordDepositModal } from './payment-modals/RecordDepositModal';
import { AddNoteModal } from './payment-modals/AddNoteModal';
import { ConfirmMarkAsPaidModal } from './payment-modals/ConfirmMarkAsPaidModal';

const DepositAttachmentDisplay = ({
  attachment,
}: {
  attachment: Attachment;
}) => {
  const { t } = useTranslation('lims');
  const url = useCachedBinaryUrl(attachment.url);

  if (!url) {
    return null;
  }

  return (
    <Group gap={4}>
      <Anchor
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        underline="hover"
        size="xs"
      >
        {attachment.title ||
          t('document', { ns: 'common', defaultValue: 'Document' })}
      </Anchor>
      <Tooltip
        label={t('reportCards.download', {
          ns: 'files-tab',
          defaultValue: 'Download',
        })}
      >
        <ActionIcon
          component="a"
          href={url}
          download={attachment.title || 'document'}
          variant="subtle"
          size="xs"
        >
          <IconDownload size={14} />
        </ActionIcon>
      </Tooltip>
    </Group>
  );
};

interface PaymentSectionProps {
  serviceRequest: ServiceRequest | undefined;
  paymentTracker: ReturnType<typeof usePaymentTracker>;
}

export const PaymentSection = ({
  serviceRequest,
  paymentTracker,
}: PaymentSectionProps) => {
  const { t } = useTranslation('lims');

  const [createOpened, { open: openCreate, close: closeCreate }] =
    useDisclosure(false);
  const [depositOpened, { open: openDeposit, close: closeDeposit }] =
    useDisclosure(false);
  const [noteOpened, { open: openNote, close: closeNote }] =
    useDisclosure(false);
  const [editOpened, { open: openEdit, close: closeEdit }] =
    useDisclosure(false);
  const [
    confirmPaidOpened,
    { open: openConfirmPaid, close: closeConfirmPaid },
  ] = useDisclosure(false);

  const {
    invoice,
    depositItems,
    paymentStatus,
    paymentSource,
    totalGross,
    discount,
    totalNet,
    depositAmount,
    remainingBalance,
    loading,
    error,
    createPayment,
    addNote,
    recordDeposit,
    markAsPaid,
    chargeDefinition,
    pricingOptions,
    mainChargeItems,
    updateSplits,
  } = paymentTracker;

  const sourceOptions = ALL_PAYMENT_SOURCES.map((s) => ({
    value: s,
    label: t(PAYMENT_SOURCE_I18N_KEYS[s] as never, { ns: 'lims' }) as string,
  }));

  if (loading && !invoice) {
    return (
      <Paper shadow="sm" p="md" withBorder>
        <Group justify="center" p="md">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            {t('payment.loading')}
          </Text>
        </Group>
      </Paper>
    );
  }

  // No payment yet (or draft) — show create button
  if (!invoice || paymentStatus === 'draft') {
    return (
      <Paper shadow="sm" p="md" withBorder>
        <Stack gap="sm">
          <Group justify="space-between">
            <Text size="sm" fw={600}>
              {t('payment.title')}
            </Text>
            <Button
              variant="outline"
              leftSection={<IconPlus size={14} />}
              onClick={openCreate}
            >
              {t('payment.createPayment')}
            </Button>
          </Group>

          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" mb="xs">
              {error}
            </Alert>
          )}

          <Text size="sm" c="dimmed">
            {t('payment.noPayment')}
          </Text>
        </Stack>

        <PaymentModal
          mode="create"
          opened={createOpened}
          onClose={closeCreate}
          chargeDefinition={chargeDefinition}
          pricingOptions={pricingOptions}
          sourceOptions={sourceOptions}
          onSave={(splits) =>
            createPayment({
              splits,
              testCode: serviceRequest?.code?.coding?.[0]?.code,
              testDisplay: serviceRequest?.code?.coding?.[0]?.display,
            })
          }
        />
      </Paper>
    );
  }

  // Payment exists — show details
  return (
    <>
      <Paper shadow="sm" p="md" withBorder>
        <Stack gap="sm">
          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red">
              {error}
            </Alert>
          )}

          <Group justify="space-between">
            <Group gap="xs">
              <IconCash size={20} />
              <Text size="sm" fw={600}>
                {t('payment.title')}
              </Text>
              <PaymentStatusBadge status={paymentStatus} />
            </Group>
            <Button
              variant="white"
              rightSection={<IconArrowNarrowRight />}
              onClick={openEdit}
              disabled={paymentStatus === 'cancelled'}
            >
              {t('payment.edit')}
            </Button>
          </Group>

          <Divider />

          {/* Payment details grid */}
          <Group grow>
            <Stack gap={4}>
              <Text size="xs" c="dimmed">
                {t('payment.labels.source')}
              </Text>
              {mainChargeItems && mainChargeItems.length > 0 ? (
                mainChargeItems.map((ci, idx) => {
                  const src = getPaymentSource({
                    extension: ci.extension,
                  } as any);
                  const amt = ci.priceOverride?.value;
                  return (
                    <Group key={idx} justify="space-between">
                      <Text size="xs">
                        {src
                          ? (t(PAYMENT_SOURCE_I18N_KEYS[src] as never, {
                              ns: 'lims',
                            }) as string)
                          : '-'}
                      </Text>
                      <Text size="xs" fw={500}>
                        {amt?.toFixed(2)} {DEFAULT_CURRENCY}
                      </Text>
                    </Group>
                  );
                })
              ) : (
                <Text size="sm" fw={500}>
                  {paymentSource
                    ? (t(PAYMENT_SOURCE_I18N_KEYS[paymentSource] as never, {
                        ns: 'lims',
                      }) as string)
                    : '-'}
                </Text>
              )}
            </Stack>

            <Stack gap={4}>
              <Text size="xs" c="dimmed">
                {t('payment.labels.amount')}
              </Text>
              <Text size="sm" fw={500}>
                {totalGross.toFixed(2)} {DEFAULT_CURRENCY}
              </Text>
            </Stack>
            <Stack gap={4}>
              <Text size="xs" c="dimmed">
                {t('payment.labels.discount')}
              </Text>
              <Text size="sm" fw={500}>
                {discount.toFixed(2)} {DEFAULT_CURRENCY}
              </Text>
            </Stack>
            <Stack gap={4}>
              <Text size="xs" c="dimmed">
                {t('payment.labels.discountedAmount')}
              </Text>
              <Text size="sm" fw={500}>
                {totalNet.toFixed(2)} {DEFAULT_CURRENCY}
              </Text>
            </Stack>
          </Group>

          {/* Deposit info */}
          {depositItems.length > 0 && (
            <>
              <Divider />
              <Group grow>
                <Stack gap={4}>
                  <Text size="xs" c="dimmed">
                    {t('payment.labels.depositsTotal')}
                  </Text>
                  <Text size="sm" fw={500} c="blue">
                    {depositAmount.toFixed(2)} {DEFAULT_CURRENCY}
                  </Text>
                </Stack>
                <Stack gap={4}>
                  <Text size="xs" c="dimmed">
                    {t('payment.labels.remaining')}
                  </Text>
                  <Text
                    size="sm"
                    fw={500}
                    c={remainingBalance > 0 ? 'orange' : 'green'}
                  >
                    {remainingBalance.toFixed(2)} {DEFAULT_CURRENCY}
                  </Text>
                </Stack>
              </Group>

              {/* Deposit history */}
              <Timeline
                active={depositItems.length - 1}
                bulletSize={20}
                lineWidth={2}
              >
                {depositItems.map((dep, idx) => {
                  const receiptAttachments = getDepositReceipts(dep);
                  return (
                    <Timeline.Item
                      key={dep.id || idx}
                      bullet={<IconReceipt size={12} />}
                      title={`${t('payment.deposit')} #${idx + 1}`}
                    >
                      <Stack gap={4}>
                        <Text size="xs" c="dimmed">
                          {dep.priceOverride?.value?.toFixed(2)}{' '}
                          {DEFAULT_CURRENCY}
                        </Text>
                        {receiptAttachments.map((att, attIdx) => (
                          <DepositAttachmentDisplay
                            key={attIdx}
                            attachment={att}
                          />
                        ))}
                      </Stack>
                    </Timeline.Item>
                  );
                })}
              </Timeline>
            </>
          )}

          {/* Notes */}
          {invoice.note && invoice.note.length > 0 && (
            <>
              <Divider />
              <Text size="xs" fw={600}>
                {t('payment.labels.notes')}
              </Text>
              <Stack gap="xs">
                {invoice.note.map((n, idx) => {
                  const parsedDate = n.time || '';
                  const text = n.text;

                  const formattedDate = parsedDate
                    ? new Date(parsedDate).toLocaleString(undefined, {
                        dateStyle: 'medium',
                        timeStyle: 'short',
                      })
                    : '';

                  return (
                    <Paper key={idx} p="xs" withBorder radius="md">
                      {formattedDate && (
                        <Text size="xs" fw={600} c="dimmed" mb={2}>
                          {formattedDate}
                        </Text>
                      )}
                      <Text size="sm">{text}</Text>
                    </Paper>
                  );
                })}
              </Stack>
            </>
          )}

          {/* Action buttons */}
          <Divider />
          <Group gap="xs">
            {paymentStatus !== 'balanced' && paymentStatus !== 'cancelled' && (
              <>
                <Button
                  variant="light"
                  size="xs"
                  leftSection={<IconPlus size={14} />}
                  onClick={openDeposit}
                >
                  {t('payment.recordDeposit')}
                </Button>
                <Button
                  variant="light"
                  color="green"
                  size="xs"
                  leftSection={<IconCheck size={14} />}
                  onClick={openConfirmPaid}
                >
                  {t('payment.markAsPaid')}
                </Button>
              </>
            )}
            <Button
              variant="subtle"
              size="xs"
              leftSection={<IconNote size={14} />}
              onClick={openNote}
            >
              {t('payment.addNote')}
            </Button>
          </Group>
        </Stack>
      </Paper>

      <ConfirmMarkAsPaidModal
        opened={confirmPaidOpened}
        onClose={closeConfirmPaid}
        markAsPaid={markAsPaid}
      />

      <PaymentModal
        mode="edit"
        opened={editOpened}
        onClose={closeEdit}
        chargeDefinition={chargeDefinition}
        pricingOptions={pricingOptions}
        sourceOptions={sourceOptions}
        mainChargeItems={mainChargeItems}
        paymentSource={paymentSource}
        totalGross={totalGross}
        onSave={updateSplits}
      />

      <RecordDepositModal
        opened={depositOpened}
        onClose={closeDeposit}
        remainingBalance={remainingBalance}
        recordDeposit={recordDeposit}
      />

      <AddNoteModal opened={noteOpened} onClose={closeNote} addNote={addNote} />
    </>
  );
};
